import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setUkFinalConfirmation,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { ErrorOutlineRounded } from "@mui/icons-material";

export default function UKFinalConfirmation() {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeclined, setIsDeclined] = useState(ukOnboarding.finalConfirmation === "declined");

  const [confirmAccount, { isLoading: isConfirming }] = api.useConfirmAccountMutation();
  const [declineAccount, { isLoading: isDeclining }] = api.useDeclineAccountMutation();

  const handleConfirm = async () => {
    try {
      await confirmAccount().unwrap();
      dispatch(setUkFinalConfirmation("confirmed"));
      setShowConfirmDialog(false);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to confirm your account. Please try again.");
      setShowConfirmDialog(false);
    }
  };

  const handleDecline = async () => {
    try {
      await declineAccount().unwrap();
      dispatch(setUkFinalConfirmation("declined"));
      setIsDeclined(true);
      setShowDeclineDialog(false);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to process your request. Please try again.");
      setShowDeclineDialog(false);
    }
  };

  if (isDeclined) {
    return (
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: "#ECEFF1",
              border: "1px solid #B0BEC5",
            }}
          >
            <CancelOutlinedIcon sx={{ fontSize: 80, color: "#78909C", mb: 2 }} />
            <Typography variant="h4" fontWeight={600} gutterBottom color="#546E7A">
              Application Closed
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your application has been closed as requested. Thank you for considering Caleb
              & Brown.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Changed Your Mind?
            </Typography>
            <Typography variant="body1" paragraph>
              If you decide you would like to invest in cryptoassets in the future, you are
              welcome to start a new application at any time.
            </Typography>
            <Typography variant="body1">
              We're here to help if you have any questions about cryptoasset investment.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={() => (window.location.href = "/")}
              sx={{ minWidth: 200 }}
            >
              Return to Home
            </Button>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: "#FFF8E1",
            border: "1px solid #FFE082",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <ErrorOutlineRounded sx={{ color: "#F57C00", fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Final Decision
              </Typography>
              <Typography variant="body1">
                The cooling-off period has ended. Please confirm whether you would like to
                proceed with activating your account or close your application.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Confirm Your Decision
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Please choose one of the options below. Both options are equally valid - choose
          what's right for you.
        </Typography>
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 2,
            height: "100%",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: 6,
              borderColor: "primary.main",
            },
          }}
          onClick={() => setShowConfirmDialog(true)}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "#4CAF50" }} />
            <Typography variant="h6" fontWeight={600}>
              Confirm Account Activation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              I have considered my decision during the cooling-off period and wish to
              proceed with activating my account to trade cryptoassets.
            </Typography>
            <Button variant="contained" color="primary" fullWidth size="large">
              Activate My Account
            </Button>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 2,
            height: "100%",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: 6,
            },
          }}
          onClick={() => setShowDeclineDialog(true)}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <CancelOutlinedIcon sx={{ fontSize: 60, color: "#78909C" }} />
            <Typography variant="h6" fontWeight={600}>
              I No Longer Want to Proceed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              I have reconsidered and decided not to proceed with opening an account to
              trade cryptoassets at this time.
            </Typography>
            <Button variant="outlined" color="inherit" fullWidth size="large">
              Close My Application
            </Button>
          </Stack>
        </Paper>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm Account Activation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            By activating your account, you confirm that:
          </Typography>
          <Stack spacing={1} mb={2}>
            <Typography variant="body2">
              • You understand the risks associated with cryptoasset investment
            </Typography>
            <Typography variant="body2">
              • You have had sufficient time to consider your decision
            </Typography>
            <Typography variant="body2">
              • You only intend to invest money you can afford to lose
            </Typography>
          </Stack>
          <Alert severity="warning">
            Remember: Cryptoassets are high-risk investments. You should be prepared to lose
            all the money you invest.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={isConfirming}
          >
            {isConfirming ? "Activating..." : "Confirm Activation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onClose={() => setShowDeclineDialog(false)}>
        <DialogTitle>Close Your Application</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to close your application?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your application will be cancelled and you will not be able to trade cryptoassets
            through this account. You can start a new application at any time in the future if
            you change your mind.
          </Typography>
          <Alert severity="info">
            There is no penalty for declining. This is a valid choice and we respect your
            decision.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeclineDialog(false)} color="inherit">
            Go Back
          </Button>
          <Button onClick={handleDecline} variant="outlined" disabled={isDeclining}>
            {isDeclining ? "Processing..." : "Close Application"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
